import React, { useState, useEffect, useCallback } from 'react';

const SLIDES = [
  // ── SLIDE 1: Title ──
  () => (
    <div className="w-full h-full bg-[#141A23] flex flex-col items-center justify-center relative">
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF3333]" />
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#FF3333]" />
      <img src="/images/img_logomark.png" alt="" className="w-20 h-20 mb-4" />
      <img src="/images/img_averpact.png" alt="AverPact" className="h-10 mb-3" />
      <p className="text-[#FF3333] text-xl font-medium mb-6">Build Certainty Into Every Deal</p>
      <div className="w-32 h-0.5 bg-[#FF3333] mb-6" />
      <p className="text-white text-lg">Enterprise Milestone-Based Escrow Platform</p>
      <p className="text-[#888] text-sm mt-3">Market Research & Competitive Analysis  |  UAE Focus  |  March 2026</p>
      <p className="text-[#888] text-xs mt-8">Confidential — For Internal & Investor Use Only</p>
    </div>
  ),

  // ── SLIDE 2: Executive Summary ──
  () => (
    <SlideLayout title="Executive Summary">
      <div className="space-y-4">
        {[
          "AverPact is a blockchain-powered, non-custodial escrow platform that automates milestone-based payments for high-value B2B contracts using smart contracts.",
          "Target industries: Oil & Gas / Construction ($2T+ annual payments), International Trade ($6T+ finance gap), Real Estate, M&A, and Government Procurement.",
          "Current escrow solutions are slow, expensive (1-3% + $5K-50K setup), and require trusted intermediaries. AverPact replaces middlemen with auditable smart contracts.",
          "UAE market is ideal: $8.9B blockchain market at 35% CAGR, progressive VARA regulation, Dubai Blockchain Strategy, and $2T+ GCC payment flows.",
          "Technology is built and tested: Solidity smart contracts on Polygon with milestone management, dispute resolution, document management, and multi-sig approvals.",
          "Unit economics: Enterprise contracts ($100K-$50M) deliver 100x better revenue per transaction than freelance escrow, with lower competition in Web3.",
        ].map((t, i) => (
          <div key={i} className="flex gap-3">
            <span className="text-[#FF3333] mt-1 flex-shrink-0">▸</span>
            <p className="text-[#141A23] text-sm leading-relaxed">{t}</p>
          </div>
        ))}
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 3: What is AverPact ──
  () => (
    <SlideLayout title="What is AverPact" subtitle="Enterprise Milestone-Based Escrow Platform">
      <p className="text-[#141A23] text-sm mb-6">A non-custodial, blockchain-powered escrow platform that automates trust and milestone payments for high-value B2B contracts.</p>
      <div className="grid grid-cols-3 gap-4">
        {[
          ["Smart Contract Escrow", "Funds locked on-chain via Solidity contracts. No banks, no custodians, no middlemen holding funds."],
          ["Milestone-Based Releases", "Contracts split into milestones. Funds released only upon dual-party or multi-sig approval."],
          ["On-Chain Audit Trail", "Every deposit, approval, release, and dispute — immutably recorded on Polygon blockchain."],
          ["Dispute Resolution", "Built-in arbitration with configurable fund splits. Evidence submission and arbitrator assignment."],
          ["Document Management", "Upload contracts, invoices, certificates. Full evidence trail for compliance and disputes."],
          ["Enterprise API Ready", "Programmable escrow via API. Integrates with ERP, procurement, and supply chain systems."],
        ].map(([title, desc], i) => (
          <div key={i} className="bg-white border border-[#e8e8e8] rounded-lg p-4 relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#FF3333]" />
            <h4 className="font-semibold text-[#141A23] text-sm mb-2">{title}</h4>
            <p className="text-[#888] text-xs leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 4: How It Works ──
  () => (
    <SlideLayout title="How It Works" subtitle="Milestone-Based Payment Flow">
      <div className="flex items-start justify-between mb-6">
        {[
          ["1", "Create Contract", "Parties define milestones, amounts, and approval conditions on-chain."],
          ["2", "Lock Funds", "Payer deposits funds into smart contract. Funds locked, not held by AverPact."],
          ["3", "Execute & Deliver", "Contractor completes milestone. Documents uploaded as proof of delivery."],
          ["4", "Multi-Party Approval", "Both parties approve completion. Multi-sig ensures accountability."],
          ["5", "Auto-Release", "Funds automatically released to contractor upon approval. On-chain record."],
          ["6", "Dispute (if needed)", "Either party opens dispute. Arbitrator resolves with configurable split."],
        ].map(([num, title, desc], i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center flex-1 min-w-0 px-1">
              <div className="w-10 h-10 rounded-full bg-[#FF3333] flex items-center justify-center text-white font-bold text-lg mb-2">{num}</div>
              <h4 className="font-semibold text-[#141A23] text-xs text-center mb-1">{title}</h4>
              <p className="text-[#888] text-[10px] text-center leading-tight">{desc}</p>
            </div>
            {i < 5 && <span className="text-[#ccc] text-xl mt-3 flex-shrink-0">→</span>}
          </React.Fragment>
        ))}
      </div>
      <div className="bg-[#f5f5f7] rounded-lg p-4">
        <h4 className="font-semibold text-[#141A23] text-sm mb-3">Why Smart Contracts Replace Traditional Escrow</h4>
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <div className="w-1 h-10 bg-[#ef4444] rounded flex-shrink-0 mt-0.5" />
            <div><span className="font-semibold text-[#141A23] text-xs">Traditional Bank Escrow: </span><span className="text-[#888] text-xs">1-3% fees + $5K-50K setup  |  Weeks to establish  |  Manual release  |  Opaque process</span></div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-1 h-10 bg-[#22c55e] rounded flex-shrink-0 mt-0.5" />
            <div><span className="font-semibold text-[#141A23] text-xs">AverPact Smart Contract: </span><span className="text-[#888] text-xs">&lt; 1% fees + gas  |  Minutes to deploy  |  Automatic release  |  Full transparency</span></div>
          </div>
        </div>
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 5: Target Industries ──
  () => (
    <SlideLayout title="Target Industries" subtitle="Five High-Value Use Cases for Milestone-Based Smart Contract Escrow">
      <div className="space-y-2.5">
        {[
          ["1. Oil & Gas / Construction", "TOP PICK", "bg-[#ef4444]", "$2T+ annual payments. Subcontracts $100K-$50M with milestone schedules. Disputes cost millions.", "100x better unit economics. Milestones already industry standard.", "#ef4444"],
          ["2. International Trade / Supply Chain", "HIGH VALUE", "bg-[#FF3333]", "$6T+ trade finance gap. Letters of Credit cost $5K-50K+ per transaction via banks.", "Smart contract replaces bank LC: goods delivered → funds released.", "#FF3333"],
          ["3. Real Estate", "HIGH VALUE", "bg-[#FF3333]", "Escrow standard in property deals. Title companies / notaries = expensive and slow.", "UAE/Dubai: massive volume, international buyers, trust issues.", "#FF3333"],
          ["4. M&A / Earn-outs", "MEDIUM", "bg-[#f59e0b]", "Business acquisitions with KPI-tied tranches. Escrow agents charge premium.", "Smart contract: if revenue > X → auto-release tranche.", "#f59e0b"],
          ["5. Government Procurement", "MEDIUM", "bg-[#f59e0b]", "Government contracts with milestone deliverables. Procurement fraud costs billions.", "On-chain transparency = anti-corruption for emerging markets.", "#f59e0b"],
        ].map(([title, badge, badgeBg, pain, adv, borderColor], i) => (
          <div key={i} className="flex bg-white border border-[#e0e0e0] rounded-lg overflow-hidden">
            <div className="w-1 flex-shrink-0" style={{ backgroundColor: borderColor as string }} />
            <div className="flex-1 p-3 flex">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-[#141A23] text-sm">{title}</h4>
                  <span className={`${badgeBg} text-white text-[9px] font-bold px-2 py-0.5 rounded`}>{badge}</span>
                </div>
                <p className="text-[#888] text-xs">{pain}</p>
              </div>
              <div className="flex-1 pl-4 border-l border-[#e0e0e0]">
                <p className="text-[#141A23] text-xs">{adv}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 6: Oil & Gas Deep Dive ──
  () => (
    <SlideLayout title="Primary Target: Oil & Gas / Construction" subtitle="The Best Pivot — Why This Market Wins">
      <div className="grid grid-cols-4 gap-3 mb-5">
        <StatCard number="$2T+" label="Annual construction payments globally" />
        <StatCard number="$100K-$50M" label="Typical subcontract value" />
        <StatCard number="$40B+" label="UAE construction market (2025)" />
        <StatCard number="28%" label="Disputes end in litigation" />
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h4 className="font-bold text-[#141A23] text-sm mb-2">THE PAIN POINT</h4>
          {["Subcontractors regularly face non-payment after completing work", "Payment disputes cost millions in legal fees and project delays", "Bank escrow costs 1-3% + months to set up per contract", "Milestone verification is manual, opaque, and easily disputed", "Multi-party payments (sub-sub-contractors) are a tracking nightmare"].map((t, i) => (
            <div key={i} className="flex gap-2 mb-1.5"><span className="text-[#ef4444] text-xs">●</span><p className="text-[#888] text-xs">{t}</p></div>
          ))}
        </div>
        <div>
          <h4 className="font-bold text-[#141A23] text-sm mb-2">WHY AVERPACT WINS HERE</h4>
          {["Milestone payments already industry standard — no behavior change", "Smart contract auto-release on dual approval eliminates trust issues", "100x better unit economics ($1M contract = $10K fee)", "On-chain audit trail satisfies compliance and reduces legal costs", "Enterprise API integrates with existing procurement/ERP systems"].map((t, i) => (
            <div key={i} className="flex gap-2 mb-1.5"><span className="text-[#22c55e] text-xs">●</span><p className="text-[#888] text-xs">{t}</p></div>
          ))}
        </div>
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 7: UAE Market — Construction & Enterprise ──
  () => (
    <SlideLayout title="UAE Market Opportunity" subtitle="Construction, Trade & Enterprise Economy">
      <div className="grid grid-cols-4 gap-3 mb-5">
        <StatCard number="$40B+" label="UAE construction market (2025)" />
        <StatCard number="94%" label="UAE businesses are SMEs" />
        <StatCard number="$50B" label="UAE outward remittances (2024)" />
        <StatCard number="$2T+" label="GCC annual payment flows" />
      </div>
      <div className="space-y-1.5">
        {[
          "UAE construction sector: $40B+ market, mega-projects (Expo legacy, NEOM adjacent, Etihad Rail, new cities)",
          "Dubai alone: $82B+ in active construction projects as of 2025",
          "SMEs = 94% of UAE businesses, 60-63% of non-oil GDP, 86% of private-sector employment",
          "250,000 new companies registered in 2025; UAE targets 2M by end of decade",
          "UAE is the GCC's largest re-export hub: $200B+ annual trade volume",
          "Cross-border payments: $50B+ in remittances, 66% prefer digital channels",
          "Real estate: Dubai transactions hit AED 760B+ (2024) — international buyers need trust infrastructure",
        ].map((t, i) => (
          <div key={i} className="flex gap-2"><span className="text-[#FF3333]">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
        ))}
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 8: UAE Blockchain & Web3 ──
  () => (
    <SlideLayout title="UAE Market Opportunity" subtitle="Blockchain & Web3 Adoption">
      <div className="grid grid-cols-4 gap-3 mb-5">
        <StatCard number="$8.9B" label="UAE blockchain market (2025)" />
        <StatCard number="35%" label="CAGR projected (2026-2032)" />
        <StatCard number="1,500+" label="Web3 companies in UAE" />
        <StatCard number="700+" label="Blockchain firms in Dubai" />
      </div>
      <div className="grid grid-cols-2 gap-8">
        <div className="space-y-1.5">
          {["VARA — World's first dedicated virtual assets regulator (Dubai)", "Dubai Blockchain Strategy: 1,800+ gov services digitized", "AED 5.5B potential savings in document processing", "Real estate tokenization pilot: AED 2.4B (2025)", "DMCC Crypto Centre: 500+ Web3 firms"].map((t, i) => (
            <div key={i} className="flex gap-2"><span className="text-[#FF3333]">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
          ))}
        </div>
        <div className="space-y-1.5">
          {["ADGM (Abu Dhabi): Enhanced digital assets framework (Jan 2026)", "Binance authorized for 3 regulated entities in Abu Dhabi", "Future Blockchain Summit: 70,000 attendees, 1,200 investors", "Blockchain startup growth: +20% annually since 2023", "Digital Dirham (CBDC) launch in late 2025"].map((t, i) => (
            <div key={i} className="flex gap-2"><span className="text-[#FF3333]">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
          ))}
        </div>
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 9: Why UAE ──
  () => (
    <SlideLayout title="Why UAE is Ideal for AverPact">
      <div className="grid grid-cols-4 gap-3 mb-5">
        <StatCard number="$89B" label="Mobile payments market (2026)" />
        <StatCard number="95%+" label="Smartphone penetration" />
        <StatCard number="146M" label="Projected real-time txns (2027)" />
        <StatCard number="$150B" label="Mobile payments by 2031" />
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        {[
          ["Progressive Regulation", "VARA provides clear licensing path. Government actively promotes blockchain for enterprise adoption."],
          ["Massive Construction Boom", "$40B+ market with mega-projects. Subcontract disputes are a daily pain point."],
          ["Cross-Border Trade Hub", "GCC's largest re-export hub. $200B+ trade volume needs modernized payment infrastructure."],
          ["Innovation Ecosystems", "DIFC, ADGM, DMCC Crypto Centre — startup-friendly environments with enterprise access."],
          ["International Buyer Base", "60% of real estate buyers are international. Trust infrastructure is critical."],
          ["Government Support", "Dubai Blockchain Strategy targets government procurement. Anti-corruption angle resonates."],
        ].map(([title, desc], i) => (
          <div key={i}>
            <h4 className="text-[#FF3333] font-semibold text-xs mb-0.5">▸ {title}</h4>
            <p className="text-[#888] text-xs pl-3">{desc}</p>
          </div>
        ))}
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 10: Target Audience ──
  () => (
    <SlideLayout title="Target Audience" subtitle="Enterprise & B2B Focus">
      <div className="grid grid-cols-2 gap-6">
        <div className="border border-[#FF3333] rounded-lg overflow-hidden">
          <div className="bg-[#FF3333] px-4 py-2"><h4 className="text-white font-semibold text-sm">PRIMARY — HIGH-VALUE B2B</h4></div>
          <div className="p-4 space-y-3 text-xs text-[#141A23]">
            <div><strong>Oil & Gas Companies & EPC Contractors</strong><br/><span className="text-[#888]">GCs, subcontractors, suppliers on $100K-$50M contracts</span></div>
            <div><strong>International Trading Companies</strong><br/><span className="text-[#888]">Importers/exporters replacing Letters of Credit</span></div>
            <div><strong>Real Estate Developers & Buyers</strong><br/><span className="text-[#888]">Escrow for off-plan purchases, land deals, JVs</span></div>
            <div><strong>M&A Advisory & Law Firms</strong><br/><span className="text-[#888]">Earn-out structuring with KPI-based auto-release</span></div>
          </div>
        </div>
        <div className="border border-[#141A23] rounded-lg overflow-hidden">
          <div className="bg-[#141A23] px-4 py-2"><h4 className="text-white font-semibold text-sm">SECONDARY — GROWTH MARKETS</h4></div>
          <div className="p-4 space-y-3 text-xs text-[#141A23]">
            <div><strong>Government Procurement Offices</strong><br/><span className="text-[#888]">Federal & emirate-level milestone contracts</span></div>
            <div><strong>SMEs & Startups</strong><br/><span className="text-[#888]">94% of UAE businesses, outsourcing to contractors</span></div>
            <div><strong>Professional Services</strong><br/><span className="text-[#888]">Legal, consulting, architecture, engineering firms</span></div>
            <div><strong>Supply Chain & Logistics</strong><br/><span className="text-[#888]">IoT-triggered payment releases for delivery confirmation</span></div>
          </div>
        </div>
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 11: Competitive — Blockchain ──
  () => (
    <SlideLayout title="Competitive Landscape" subtitle="Blockchain & Escrow Platforms">
      <ComparisonTable
        headers={["Platform", "Type", "Escrow", "Milestones", "Non-Custodial", "Dispute", "Enterprise"]}
        rows={[
          ["AverPact", "B2B Smart Escrow", "✓ Smart Contract", "✓ Built-in", "✓ Fully", "✓ Arbitration", "✓ API + Multi-sig"],
          ["Escrow.com", "Traditional", "✓ Custodial", "✗", "✗ Custodial", "✓ Manual", "Partial"],
          ["Kleros", "Decentralized Court", "Partial", "✗", "✓", "✓ Jury", "✗"],
          ["Request Network", "Payment Protocol", "✓ Smart Contract", "✗", "✓", "✓ Basic", "Partial"],
          ["Smart Invoice", "Blockchain Invoice", "✓ Smart Contract", "✓", "✓", "✓ Arbitrator", "✗"],
          ["Bank LC", "Trade Finance", "✓ Custodial", "Partial", "✗", "✓ Bank", "✓ Standard"],
        ]}
        highlightRow={0}
      />
      <div className="mt-3">
        <p className="text-[#141A23] font-semibold text-xs">Key Insight: <span className="font-normal text-[#888]">No blockchain platform offers enterprise-grade, milestone-based escrow with multi-sig approval, dispute resolution, and document management — purpose-built for construction, trade, and real estate contracts.</span></p>
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 12: Competitive — Traditional ──
  () => (
    <SlideLayout title="Competitive Landscape" subtitle="Traditional Enterprise Payment & Escrow Solutions">
      <ComparisonTable
        headers={["Solution", "Type", "Fees", "Blockchain", "Milestones", "Speed", "Limitation"]}
        rows={[
          ["AverPact", "Smart Contract", "< 1% + gas", "✓ Polygon", "✓ Built-in", "Minutes", "Pre-launch"],
          ["Bank LC", "Trade Finance", "$5K-50K+/txn", "✗", "Partial", "Weeks", "Slow, expensive"],
          ["Escrow.com", "Online Escrow", "1-3%", "✗", "✗", "Days", "No milestones"],
          ["Bank Escrow", "Traditional", "1-3% + setup", "✗", "Manual", "Weeks", "Slow setup"],
          ["Title Companies", "Real Estate", "0.5-2%", "✗", "✗", "Weeks", "RE only"],
        ]}
        highlightRow={0}
      />
      <div className="mt-4 bg-[#f5f5f7] rounded-lg p-3">
        <p className="text-[#141A23] font-semibold text-xs mb-1">Cost Comparison — $1M Contract:</p>
        <p className="text-[#888] text-xs">Bank escrow: $10,000-30,000 + weeks setup  |  Letter of Credit: $5,000-50,000+  |  <span className="text-[#FF3333] font-semibold">AverPact: ~$10,000 (1% + gas) — instant setup, auto-release</span></p>
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 13: Feature Matrix ──
  () => (
    <SlideLayout title="Feature Comparison Matrix" subtitle="AverPact vs Enterprise Escrow Solutions">
      <ComparisonTable
        headers={["Feature", "AverPact", "Bank Escrow", "Bank LC", "Escrow.com", "Kleros", "Smart Invoice"]}
        rows={[
          ["Smart Contract Escrow", "✓", "✗", "✗", "✗", "Partial", "✓"],
          ["Milestone Payments", "✓", "Manual", "Partial", "✗", "✗", "✓"],
          ["Non-Custodial", "✓", "✗", "✗", "✗", "✓", "✓"],
          ["Dispute Resolution", "✓", "✓ Legal", "✓ Bank", "✓", "✓", "✓"],
          ["Document Management", "✓", "✗", "Partial", "✗", "✗", "✗"],
          ["On-Chain Audit Trail", "✓", "✗", "✗", "✗", "✓", "✓"],
          ["Multi-Sig Approval", "✓", "✗", "✗", "✗", "✗", "✗"],
          ["Enterprise API", "✓", "✗", "Partial", "Partial", "✗", "✗"],
          ["Setup Time", "Minutes", "Weeks", "Weeks", "Hours", "Minutes", "Minutes"],
          ["Cost", "< 1%", "1-3%+", "$5K-50K+", "1-3%", "Variable", "Gas only"],
        ]}
        highlightCol={1}
      />
    </SlideLayout>
  ),

  // ── SLIDE 14: SWOT ──
  () => (
    <SlideLayout title="SWOT Analysis">
      <div className="grid grid-cols-2 gap-4">
        <SwotBox color="#22c55e" title="STRENGTHS" items={[
          "Non-custodial smart contract escrow — no trust required",
          "Milestone-based payments with multi-sig approval",
          "100x better unit economics targeting enterprise contracts",
          "On-chain audit trail satisfies compliance requirements",
          "Built-in dispute resolution eliminates legal overhead",
        ]} />
        <SwotBox color="#ef4444" title="WEAKNESSES" items={[
          "Pre-launch — no enterprise customer traction yet",
          "Requires crypto/stablecoin adoption by enterprise parties",
          "Currently testnet only (Polygon Amoy)",
          "No fiat on/off-ramp yet (planned)",
          "Enterprise sales cycle is long and relationship-driven",
        ]} />
        <SwotBox color="#388bfd" title="OPPORTUNITIES" items={[
          "UAE construction boom: $40B+ market with mega-projects",
          "$6T+ global trade finance gap — massive underserved demand",
          "VARA provides clear regulatory pathway for enterprise adoption",
          "Stablecoin (USDT) eliminates crypto volatility objection",
          "Enterprise API opens ERP/procurement integrations",
          "Government anti-corruption aligns with on-chain transparency",
        ]} />
        <SwotBox color="#f59e0b" title="THREATS" items={[
          "Regulatory changes — VARA/CMA compliance evolution",
          "Banks developing blockchain-based LC alternatives",
          "Enterprise crypto skepticism and legacy system inertia",
          "Smart contract vulnerabilities / security risks at scale",
          "Established enterprise players adding blockchain features",
          "Long enterprise sales cycles delay revenue generation",
        ]} />
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 15: Regulatory ──
  () => (
    <SlideLayout title="Regulatory Landscape" subtitle="UAE Virtual Asset & Payment Regulations">
      <div className="grid grid-cols-3 gap-4">
        <RegBox color="#FF3333" title="VARA (Dubai)" items={[
          "World's first dedicated VA regulator",
          "AverPact likely classified as VASP",
          "VASP license required for escrow",
          "Travel Rule: mandatory for txns >AED 3,500",
          "All 12 Rulebooks updated (2026)",
          "Hard deadline: September 2026",
          "Penalty: up to AED 1B",
        ]} />
        <RegBox color="#141A23" title="CMA (Federal)" items={[
          "SCA renamed to CMA (Jan 2026)",
          "Virtual assets = 'Financial Products'",
          "Regulates VA trading platforms",
          "Federal scope (excl. free zones)",
          "1-year transition period",
          "Compliance deadline: Jan 2027",
          "Applies alongside VARA in Dubai",
        ]} />
        <RegBox color="#388bfd" title="CBUAE (Central Bank)" items={[
          "Sole regulator for payment tokens",
          "PTSR effective June 2025",
          "Stablecoins = 'payment tokens'",
          "Only dirham-backed for local payments",
          "Algorithmic stablecoins prohibited",
          "AE Coin first approved (Dec 2024)",
          "Critical for USDT integration path",
        ]} />
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 16: AML/KYC ──
  () => (
    <SlideLayout title="Regulatory Compliance" subtitle="AML/KYC Requirements & Recommendations">
      <div className="grid grid-cols-2 gap-8">
        <div>
          <h4 className="font-bold text-[#141A23] text-sm mb-3">AML/KYC Requirements for Enterprise</h4>
          {["KYC verification for all entities (3-tier process)", "Enhanced due diligence for high-value contracts & PEPs", "Travel Rule: transfer data for txns >AED 3,500", "Real-time transaction monitoring & SAR reporting", "Record retention: minimum 8 years", "Corporate KYB (Know Your Business) for enterprise users", "Hard compliance deadline: September 2026"].map((t, i) => (
            <div key={i} className="flex gap-2 mb-1.5"><span className="text-[#141A23]">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
          ))}
        </div>
        <div>
          <h4 className="font-bold text-[#FF3333] text-sm mb-3">Recommendations for AverPact</h4>
          {["1. Apply for VARA VASP license (Dubai)", "2. Implement KYC/KYB with Sumsub or Jumio", "3. Integrate Travel Rule (Notabene, Chainalysis)", "4. Add AML transaction monitoring from day one", "5. Consider ADGM as additional jurisdiction", "6. Engage UAE crypto-specialized legal counsel", "7. Plan USDT integration via CBUAE pathway", "8. Build 8-year record retention system"].map((t, i) => (
            <div key={i} className="flex gap-2 mb-1.5"><span className="text-[#FF3333]">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
          ))}
        </div>
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 17: Go-to-Market ──
  () => (
    <SlideLayout title="Go-to-Market Strategy" subtitle="Phased Enterprise Launch Plan">
      <div className="grid grid-cols-3 gap-4">
        {[
          ["Phase 1: Foundation", "Q2-Q3 2026", "#FF3333", ["Polygon mainnet deployment", "VARA license application", "USDT/stablecoin integration", "KYC/KYB + AML compliance", "Smart contract security audit", "Enterprise API development"]],
          ["Phase 2: Pilot", "Q4 2026 - Q1 2027", "#141A23", ["3-5 pilot construction companies", "Target: $10M+ escrow volume", "DMCC / DIFC partnership", "UAE construction industry events", "Case studies from pilot clients", "Iterate on enterprise UX/API"]],
          ["Phase 3: Scale", "2027-2028", "#388bfd", ["Expand to trade finance & RE", "GCC expansion (Saudi, Bahrain)", "Fiat on/off-ramp integration", "ERP integrations (SAP, Oracle)", "Target: $100M+ TVL, 50+ enterprises", "Platform fee: 0.5-1% on releases"]],
        ].map(([title, period, color, items], i) => (
          <div key={i} className="border rounded-lg overflow-hidden" style={{ borderColor: color as string }}>
            <div className="px-4 py-2.5" style={{ backgroundColor: color as string }}>
              <h4 className="text-white font-semibold text-sm">{title}</h4>
              <p className="text-white/70 text-xs">{period}</p>
            </div>
            <div className="p-4 space-y-2">
              {(items as string[]).map((t, j) => (
                <div key={j} className="flex gap-2"><span className="text-[#FF3333] text-xs">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 18: Risks ──
  () => (
    <SlideLayout title="Key Risks & Challenges">
      <div className="space-y-2.5">
        {[
          ["Regulatory Risk", "HIGH", "#ef4444", "VARA/CMA licensing may delay launch. Enterprise clients require full compliance.", "Engage legal counsel Q2 2026. Initiate VARA application immediately."],
          ["Smart Contract Risk", "HIGH", "#ef4444", "High-value contracts amplify smart contract vulnerability impact.", "CertiK/OpenZeppelin audit. Bug bounty. Insurance coverage."],
          ["Enterprise Adoption", "MEDIUM", "#f59e0b", "Construction/trade firms conservative on crypto. Long sales cycles.", "Stablecoin-first (USDT). White-glove onboarding. Start with crypto-friendly."],
          ["Market Education", "MEDIUM", "#f59e0b", "Enterprise users unfamiliar with blockchain escrow.", "Pilot programs with measurable ROI. Case studies. Industry partnerships."],
          ["Competitive Response", "LOW", "#22c55e", "Banks developing blockchain LC. SAP/Oracle adding features.", "Speed to market. Niche construction focus. Cost advantage."],
        ].map(([name, level, color, desc, mitigation], i) => (
          <div key={i} className="flex bg-white border border-[#e0e0e0] rounded-lg overflow-hidden">
            <div className="w-1 flex-shrink-0" style={{ backgroundColor: color as string }} />
            <div className="flex-1 p-3 flex">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-[#141A23] text-sm">{name}</h4>
                  <span className="text-white text-[9px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: color as string }}>{level}</span>
                </div>
                <p className="text-[#888] text-xs">{desc}</p>
              </div>
              <div className="flex-1 pl-4 border-l border-[#e0e0e0]">
                <p className="text-[#141A23] text-xs"><strong>Mitigation:</strong> {mitigation}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SlideLayout>
  ),

  // ── SLIDE 19: Summary ──
  () => (
    <SlideLayout title="Summary & Next Steps">
      <div className="grid grid-cols-2 gap-8 mb-6">
        <div>
          <h4 className="font-bold text-[#141A23] text-sm mb-3">Key Takeaways</h4>
          {[
            "Enterprise B2B escrow: 100x better unit economics than freelance",
            "Oil & Gas / Construction: $2T+ market, milestones already standard",
            "UAE is perfect: blockchain regulation, construction boom, trade hub",
            "Smart contracts replace bank escrow: faster, cheaper, transparent",
            "Technology built and tested: milestones, disputes, docs, multi-sig",
          ].map((t, i) => (
            <div key={i} className="flex gap-2 mb-2"><span className="text-[#FF3333]">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
          ))}
        </div>
        <div>
          <h4 className="font-bold text-[#FF3333] text-sm mb-3">Immediate Next Steps</h4>
          {[
            "1. Smart contract security audit (CertiK / OpenZeppelin)",
            "2. VARA VASP license application",
            "3. USDT stablecoin integration",
            "4. Enterprise API & SDK development",
            "5. Polygon mainnet deployment",
            "6. 3-5 construction company pilot partnerships",
            "7. KYC/KYB/AML compliance integration",
            "8. Seed funding round ($1M-$2M target)",
          ].map((t, i) => (
            <div key={i} className="flex gap-2 mb-2"><span className="text-[#FF3333]">▸</span><p className="text-[#141A23] text-xs">{t}</p></div>
          ))}
        </div>
      </div>
      <div className="bg-[#141A23] rounded-xl p-5 flex items-center justify-center gap-4">
        <img src="/images/img_logomark.png" alt="" className="w-10 h-10" />
        <div className="text-center">
          <p className="text-[#FF3333] font-bold text-xl">AVERPACT — Build Certainty Into Every Deal</p>
          <p className="text-white/80 text-sm mt-1">averpact.io  |  Enterprise Milestone Escrow</p>
        </div>
      </div>
    </SlideLayout>
  ),
];

// ── Sub-components ──

function SlideLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="w-full h-full bg-white flex flex-col relative">
      <div className="h-1 bg-[#FF3333] flex-shrink-0" />
      <div className="flex items-center gap-3 px-8 pt-4 pb-2 flex-shrink-0">
        <img src="/images/img_logomark.png" alt="" className="w-8 h-8" />
        <div>
          <h2 className="font-bold text-[#141A23] text-xl leading-tight">{title}</h2>
          {subtitle && <p className="text-[#888] text-xs">{subtitle}</p>}
        </div>
      </div>
      <div className="flex-1 px-8 pb-4 overflow-hidden">{children}</div>
      <div className="h-6 bg-[#f8f8f8] flex items-center justify-between px-8 flex-shrink-0">
        <span className="text-[#888] text-[9px]">AverPact — Confidential  |  averpact.io</span>
      </div>
    </div>
  );
}

function StatCard({ number, label }: { number: string; label: string }) {
  return (
    <div className="bg-white border border-[#e0e0e0] rounded-lg p-3 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#FF3333]" />
      <p className="text-[#FF3333] font-bold text-lg">{number}</p>
      <p className="text-[#888] text-[10px] mt-0.5">{label}</p>
    </div>
  );
}

function ComparisonTable({ headers, rows, highlightRow, highlightCol }: { headers: string[]; rows: string[][]; highlightRow?: number; highlightCol?: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>{headers.map((h, i) => <th key={i} className="bg-[#141A23] text-white px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className={r === highlightRow ? 'bg-[#f5f5f7]' : r % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
              {row.map((cell, c) => (
                <td key={c} className={`px-3 py-1.5 border-b border-[#eee] ${c === highlightCol ? 'font-semibold text-[#FF3333]' : 'text-[#141A23]'} ${cell === '✓' || cell.startsWith('✓') ? 'text-[#22c55e]' : ''} ${cell === '✗' || cell.startsWith('✗') ? 'text-[#ccc]' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SwotBox({ color, title, items }: { color: string; title: string; items: string[] }) {
  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: color }}>
      <div className="px-4 py-1.5" style={{ backgroundColor: color }}><h4 className="text-white font-semibold text-sm">{title}</h4></div>
      <div className="p-3 space-y-1">
        {items.map((t, i) => <p key={i} className="text-[#141A23] text-[11px]">• {t}</p>)}
      </div>
    </div>
  );
}

function RegBox({ color, title, items }: { color: string; title: string; items: string[] }) {
  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: color }}>
      <div className="px-4 py-2" style={{ backgroundColor: color }}><h4 className="text-white font-semibold text-sm">{title}</h4></div>
      <div className="p-3 space-y-1.5">
        {items.map((t, i) => <div key={i} className="flex gap-2"><span style={{ color }}>▸</span><p className="text-[#141A23] text-xs">{t}</p></div>)}
      </div>
    </div>
  );
}

// ── Main Component ──

const PitchDeckPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const total = SLIDES.length;

  const goNext = useCallback(() => setCurrentSlide(s => Math.min(s + 1, total - 1)), [total]);
  const goPrev = useCallback(() => setCurrentSlide(s => Math.max(s - 1, 0)), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); goPrev(); }
      if (e.key === 'Home') { e.preventDefault(); setCurrentSlide(0); }
      if (e.key === 'End') { e.preventDefault(); setCurrentSlide(total - 1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext, goPrev, total]);

  const SlideComponent = SLIDES[currentSlide];

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center justify-center p-4 select-none">
      {/* Slide container — 16:9 */}
      <div className="w-full max-w-[1200px] aspect-[16/9] bg-white rounded-lg shadow-2xl overflow-hidden relative">
        <SlideComponent />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 mt-4">
        <button onClick={goPrev} disabled={currentSlide === 0} className="px-4 py-2 bg-[#141A23] text-white rounded-lg text-sm font-medium disabled:opacity-30 hover:bg-[#5a2200] transition-colors">
          ← Prev
        </button>
        <div className="flex items-center gap-1.5">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => setCurrentSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-[#FF3333] w-6' : 'bg-[#666] hover:bg-[#888]'}`} />
          ))}
        </div>
        <span className="text-[#888] text-sm font-mono min-w-[60px] text-center">{currentSlide + 1} / {total}</span>
        <button onClick={goNext} disabled={currentSlide === total - 1} className="px-4 py-2 bg-[#FF3333] text-white rounded-lg text-sm font-medium disabled:opacity-30 hover:bg-[#e55a04] transition-colors">
          Next →
        </button>
      </div>
      <p className="text-[#666] text-xs mt-2">Use arrow keys or spacebar to navigate</p>
    </div>
  );
};

export default PitchDeckPage;
